import type { MeContext, PermissionKey } from '@didto/domain';
import type { Session } from '@supabase/supabase-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMe } from '@/lib/api';
import { useDeviceStore } from '@/lib/deviceStore';
import type { LinkError } from '@/lib/passwordRecovery';
import {
  forgetRecoverySession,
  isRecoverySession,
  landingLinkError,
  markRecoverySession,
} from '@/lib/passwordRecovery';
import { supabase } from '@/lib/supabase';
import { toAppError } from '@/lib/errors';

/**
 * Session context: the Supabase auth state plus the server-resolved `me()` context.
 * `principal` tells the shell which world we are in:
 *   human  → owner/manager browser login (email + password, TOTP step-up)
 *   device → enrolled tablet; a staff PIN session may or may not be open
 *   none   → nobody (login or enrol)
 */
export interface SessionState {
  ready: boolean;
  authed: boolean;
  me: MeContext | null;
  error: string | null;
  permissions: ReadonlySet<PermissionKey>;
  can: (key: PermissionKey) => boolean;
  /**
   * ADR-025: whether this session may change the given employee's account. An owner account (one
   * holding the OWNER role) changes only from an owner's signed-in account, never from a tablet.
   * The server enforces this; the app only hides what would be refused.
   */
  canManageEmployee: (employeeId: string) => boolean;
  /** ADR-025: whether this session may make someone an owner or change the OWNER role. */
  canActOnOwners: boolean;
  /**
   * ADR-041: this browser is signed in with the session a password reset link opened. The app keeps
   * it on the reset page until the password is changed or the reset is cancelled.
   */
  recovery: boolean;
  /** ADR-041: the app was opened with a reset link that failed (expired or already used). */
  linkError: LinkError | null;
  /** Forgets the reset link: its session's mark and a failed link's error. */
  endRecovery: () => void;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [recovery, setRecovery] = useState(false);
  const [linkError, setLinkError] = useState<LinkError | null>(() => landingLinkError());
  const sessionToken = useDeviceStore((s) => s.sessionToken);

  useEffect(() => {
    let alive = true;
    // Known only once the client has read the address the app was opened with (getSession waits
    // for it), so nothing navigates away from a reset link before its session exists.
    const apply = (s: Session | null) => {
      setAuthed(!!s);
      setRecovery(isRecoverySession(s?.access_token));
    };
    supabase.auth.getSession().then(({ data }) => alive && apply(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((evt, s) => {
      if (evt === 'PASSWORD_RECOVERY') markRecoverySession(s?.access_token);
      if (evt === 'SIGNED_OUT') forgetRecoverySession();
      apply(s);
      // Never call Supabase inside this callback (it runs under the auth lock); defer the refetch.
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['me'] }), 0);
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, [qc]);

  const meQuery = useQuery({
    queryKey: ['me', sessionToken],
    queryFn: () => fetchMe(),
    enabled: authed === true,
    staleTime: 30_000,
    retry: 2,
  });

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['me'] });
  }, [qc]);

  const signOut = useCallback(async () => {
    useDeviceStore.getState().clear();
    await supabase.auth.signOut();
    qc.clear();
  }, [qc]);

  const endRecovery = useCallback(() => {
    forgetRecoverySession();
    setRecovery(false);
    setLinkError(null);
  }, []);

  const value = useMemo<SessionState>(() => {
    const me = meQuery.data ?? null;
    const permissions = new Set<PermissionKey>((me?.permissions ?? []) as PermissionKey[]);
    const canActOnOwners = me?.principal === 'human' && me.is_owner === true;
    const owners = new Set(me?.owner_employee_ids ?? []);
    return {
      ready: authed !== null && (authed === false || meQuery.isSuccess || meQuery.isError),
      authed: authed === true,
      me,
      error: meQuery.error ? toAppError(meQuery.error).message : null,
      permissions,
      can: (key) => permissions.has(key),
      canManageEmployee: (employeeId) =>
        permissions.has('employee.manage') && (canActOnOwners || !owners.has(employeeId)),
      canActOnOwners,
      recovery: authed === true && recovery,
      linkError,
      endRecovery,
      refresh,
      signOut,
    };
  }, [
    authed,
    recovery,
    linkError,
    meQuery.data,
    meQuery.isSuccess,
    meQuery.isError,
    meQuery.error,
    endRecovery,
    refresh,
    signOut,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
